import React, { useEffect, useState, useRef } from "react";
import html2pdf from "html2pdf.js";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { closeGlobalPopup, fetchBill, fetchBillItems, openGlobalPopup } from "../../redux/action";
import SmsPopup from "../../component/SMS/SmsPopup";
import PrintBill from "../../component/PrintBill/PrintBill";
import './bill.css';
import BackButton from "../../component/BackButton/BackButton";
import { advancedBillingApi } from "../../services/advancedBillingApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Download, FileSpreadsheet, Printer } from "lucide-react";
import { createCsv } from "../Reports/reportUtils";
import { getTerminalId } from "../../Utills/terminalIdentity";
import { auditActionLabel, auditFieldLabel, createAuditExportRows, formatAuditValue, getAuditChanges } from "./auditUtils";
import GlobalPopup from "../../component/Popup/GlobalPopup";
import PaymentAllocationEditor, { getPaymentValidationError, summarizePayments } from "../../component/Billing/PaymentAllocationEditor";
import { ButtonLoader, PageLoader } from "../../component/common/Loader";
import PasswordInput from "../../component/common/PasswordInput";

const RefundReceipt = React.forwardRef(({ data }, ref) => {
  const { t } = useTranslation();
  if (!data) return null;
  const refund = data.refund || {};
  return <div className="refund-receipt" ref={ref}>
    <h2>{t("Refund receipt")}</h2>
    <p><strong>{t("Receipt")}:</strong> {data.receiptNumber}</p>
    <p><strong>{t("Bill")}:</strong> {data.billNumber || "—"} · {data.tableNumber || "—"}</p>
    <p><strong>{t("Date")}:</strong> {refund.createdAt ? new Date(refund.createdAt).toLocaleString() : "—"}</p>
    {(data.items || []).length > 0 && <table><thead><tr><th>{t("Item")}</th><th>{t("Qty")}</th><th>{t("Amount")}</th></tr></thead><tbody>
      {data.items.map(item => <tr key={item.billRefundItemId}><td>{item.itemName}</td><td>{item.quantity}</td><td>₹{Number(item.refundAmount).toFixed(2)}</td></tr>)}
    </tbody></table>}
    <p><strong>{t("Refund mode")}:</strong> {refund.refundMode}</p>
    <p><strong>{t("Status")}:</strong> {t(refund.refundStatus)}</p>
    <p><strong>{t("Refunded amount")}:</strong> ₹{Number(refund.amount || 0).toFixed(2)}</p>
    <p><strong>{t("Reason")}:</strong> {refund.reason}</p>
    {refund.referenceNumber && <p><strong>{t("Reference")}:</strong> {refund.referenceNumber}</p>}
  </div>;
});

const AuditReport = React.forwardRef(({ audit, billNumber, t }, ref) => {
  const reprintCount = audit.filter(entry => entry.action === "BILL_REPRINTED").length;
  return <div className="bill-audit-report" ref={ref}>
    <header className="audit-report-heading">
      <div><small>{t("CONTROL REPORT")}</small><h3>{t("Audit history")}</h3><p>{t("Bill")} #{billNumber || "—"}</p></div>
      <div className="audit-count"><strong>{reprintCount}</strong><span>{t("Reprints")}</span></div>
    </header>
    {audit.length ? <div className="audit-timeline">{audit.map(entry => {
      const changes = getAuditChanges(entry);
      return <article className="audit-entry" key={entry.billingAuditLogId}>
        <div className="audit-entry-heading">
          <div><strong>{t(auditActionLabel(entry.action))}</strong>{entry.reprintNumber && <span className="audit-reprint-badge">{t("Reprint")} #{entry.reprintNumber}</span>}</div>
          <time>{new Date(entry.createdAt).toLocaleString()}</time>
        </div>
        <dl className="audit-meta">
          <div><dt>{t("Performed by")}</dt><dd>{entry.performedByName || (entry.performedByUserId ? `${t("User")} #${entry.performedByUserId}` : t("System"))}</dd></div>
          <div><dt>{t("Approved by")}</dt><dd>{entry.approvedByName || (entry.approvedByUserId ? `${t("User")} #${entry.approvedByUserId}` : "—")}</dd></div>
          <div><dt>{t("Terminal")}</dt><dd>{entry.terminalId || t("Not recorded")}</dd></div>
        </dl>
        {entry.reason && <p className="audit-reason"><strong>{t("Reason")}:</strong> {entry.reason}</p>}
        {changes.length > 0 ? <div className="audit-change-table" role="table" aria-label={t("Old and new values")}>
          <div className="audit-change-head" role="row"><span>{t("Field")}</span><span>{t("Old value")}</span><span>{t("New value")}</span></div>
          {changes.map(change => <div className="audit-change-row" role="row" key={change.field}>
            <strong>{t(auditFieldLabel(change.field))}</strong>
            <span data-label={t("Old value")}>{t(formatAuditValue(change.before, change.field))}</span>
            <span data-label={t("New value")}>{t(formatAuditValue(change.after, change.field))}</span>
          </div>)}
        </div> : <p className="audit-no-change">{t(entry.action === "BILL_REPRINTED" ? "Bill values were unchanged for this reprint." : "No value changes recorded.")}</p>}
      </article>;
    })}</div> : <p>{t("No audit activity recorded.")}</p>}
  </div>;
});

const ViewBill = () => {
  const { t } = useTranslation();
  const [showSmsPopup, setShowSmsPopup] = useState(false);
  const OrderType = "DINE_IN";
  const [payments, setPayments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [refundHistory, setRefundHistory] = useState([]);
  const [refundReceipt, setRefundReceipt] = useState(null);
  const [reprintReason, setReprintReason] = useState("");
  const [refund, setRefund] = useState({ amount: "", refundMode: "CASH", reason: "", referenceNumber: "", managerPin: "", items: [] });
  const [voidRequest, setVoidRequest] = useState({ reason: "", managerPin: "", items: [] });
  const [approvalPin, setApprovalPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [auditExporting, setAuditExporting] = useState("");
  const [paymentCorrection, setPaymentCorrection] = useState({ payments: [], reason: "", managerPin: "" });
  const [terminalId] = useState(getTerminalId);

  const dispatch = useDispatch();
  const componentRef = useRef();
  const refundReceiptRef = useRef();
  const auditReportRef = useRef();
  const { billId, tableName } = useParams();

  const billTaxDetails = useSelector((state) => state?.admin?.bill);
  const billItems = useSelector((state) => state?.admin?.billItem);

  // 1. Fetch both bill details and bill items on mount / page refresh
  useEffect(() => {
    if (billId) {
      const isViewBill =1;
      const data = {billId, isViewBill}
      dispatch(fetchBill(data));
      dispatch(fetchBillItems(billId));
      Promise.all([advancedBillingApi.getPayments(billId), advancedBillingApi.getAudit(billId), advancedBillingApi.getRefunds(billId)])
        .then(([paymentRows, auditRows, refundRows]) => { setPayments(paymentRows || []); setAudit(auditRows || []); setRefundHistory(refundRows || []); })
        .catch(error => toast.error(error.message));
    }
  }, [dispatch, billId]);

  // Historical bills must render their persisted financial snapshot. Recalculating
  // from current menu/tax settings changes old receipts when settings are edited.
  const itemSubTotal = Array.isArray(billItems)
    ? billItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.price || 0), 0)
    : 0;
  const subTotal = Number(billTaxDetails?.subTotal ?? itemSubTotal);

  // Extract tax/charge values with strict default fallbacks to 0
  const cgstAmount = billTaxDetails?.cgstAmount || 0;
  const sgstAmount = billTaxDetails?.sgstAmount || 0;
  const serviceChargeAmount = billTaxDetails?.serviceChargeAmount || 0;
  const discountAmount = billTaxDetails?.discountAmount || 0;
  const settlementAmount = billTaxDetails?.settlementAmount || 0;

  const fallbackGrandTotal = Math.max(subTotal + cgstAmount + sgstAmount + serviceChargeAmount +
    Number(billTaxDetails?.packagingCharge || 0) + Number(billTaxDetails?.deliveryCharge || 0) - discountAmount, 0);
  const grandTotal = Number(billTaxDetails?.grandTotal ?? fallbackGrandTotal);
  const roundedTotal = Math.round(grandTotal);
  const roundOff = Number((roundedTotal - grandTotal).toFixed(2));

  const handleDownload = () => {
    const element = componentRef.current;
    const options = {
      margin: 5,
      filename: `Bill-${billTaxDetails?.billNumber || "details"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
    };
    html2pdf().from(element).set(options).save();
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef
  });
  const handleRefundPrint = useReactToPrint({ contentRef: refundReceiptRef });
  const handleAuditPrint = useReactToPrint({
    contentRef: auditReportRef,
    bodyClass: "bill-audit-print",
    documentTitle: () => `bill-${billTaxDetails?.billNumber || billId}-audit`
  });

  const handleRecordedPrint = async () => {
    if (!reprintReason.trim()) return toast.error("Reprint reason is required");
    try {
      setSaving(true);
      const result = await advancedBillingApi.recordReprint(billId, reprintReason, terminalId);
      setAudit(await advancedBillingApi.getAudit(billId) || []);
      setReprintReason("");
      if (result?.reprintCount) toast.success(`${t("Reprint")} #${result.reprintCount} ${t("recorded")}`);
      handlePrint();
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const submitRefund = async event => {
    event.preventDefault();
    try {
      setSaving(true);
      const result = await advancedBillingApi.refundBill(billId, { ...refund, terminalId, amount: Number(refund.amount || 0) });
      setRefund({ amount: "", refundMode: "CASH", reason: "", referenceNumber: "", managerPin: "", items: [] });
      const [auditRows, refundRows] = await Promise.all([advancedBillingApi.getAudit(billId), advancedBillingApi.getRefunds(billId)]);
      setAudit(auditRows || []);
      setRefundHistory(refundRows || []);
      setRefundReceipt(result);
      toast.success(result?.refund?.refundStatus === "Failed" ? "Refund attempt recorded as failed" : `Refund ${result?.refund?.refundStatus?.toLowerCase() || "recorded"}`);
      dispatch(fetchBill({ billId, isViewBill: 1 }));
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const updateRefundItem = (billItemId, quantity) => {
    const parsed = Math.max(0, Number(quantity) || 0);
    setRefund(current => ({ ...current, items: parsed > 0
      ? [...current.items.filter(item => item.billItemId !== billItemId), { billItemId, quantity: parsed }]
      : current.items.filter(item => item.billItemId !== billItemId) }));
  };

  const updateVoidItem = (billItemId, quantity) => {
    const parsed = Math.max(0, Number(quantity) || 0);
    setVoidRequest(current => ({ ...current, items: parsed > 0
      ? [...current.items.filter(item => item.billItemId !== billItemId), { billItemId, quantity: parsed }]
      : current.items.filter(item => item.billItemId !== billItemId) }));
  };

  const submitItemVoid = async event => {
    event.preventDefault();
    if (!voidRequest.items.length) return toast.error("Select at least one item quantity to void");
    try {
      setSaving(true);
      await advancedBillingApi.voidBill(billId, { ...voidRequest, terminalId });
      setVoidRequest({ reason: "", managerPin: "", items: [] });
      const [auditRows] = await Promise.all([advancedBillingApi.getAudit(billId)]);
      setAudit(auditRows || []);
      dispatch(fetchBill({ billId, isViewBill: 1 }));
      dispatch(fetchBillItems(billId));
      toast.success("Selected bill quantity voided");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const saveApprovalPin = async event => {
    event.preventDefault();
    try {
      setSaving(true);
      await advancedBillingApi.setManagerApprovalPin(approvalPin);
      setApprovalPin("");
      toast.success("Manager refund PIN saved");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const printRefundReceipt = async refundId => {
    try {
      setRefundReceipt(await advancedBillingApi.getRefundReceipt(refundId));
      setTimeout(() => handleRefundPrint(), 0);
    } catch (error) { toast.error(error.message); }
  };

  const refreshRefund = async refundId => {
    try {
      setSaving(true);
      await advancedBillingApi.refreshRefund(refundId);
      setRefundHistory(await advancedBillingApi.getRefunds(billId) || []);
      setAudit(await advancedBillingApi.getAudit(billId) || []);
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const correctionPayable = Number(payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0).toFixed(2));
  const openPaymentCorrection = () => {
    setPaymentCorrection({
      payments: payments.map(payment => ({
        paymentMode: payment.paymentMode || "",
        amount: Number(payment.amount || 0),
        tenderedAmount: Number(payment.tenderedAmount || payment.amount || 0),
        referenceNumber: payment.referenceNumber || ""
      })),
      reason: "",
      managerPin: ""
    });
    dispatch(openGlobalPopup("payment-correction"));
  };
  const closePaymentCorrection = () => setPaymentCorrection({ payments: [], reason: "", managerPin: "" });
  const submitPaymentCorrection = async event => {
    event.preventDefault();
    const validationError = getPaymentValidationError(paymentCorrection.payments, correctionPayable);
    if (validationError) return toast.error(t(validationError));
    if (!paymentCorrection.reason.trim()) return toast.error(t("Payment correction reason is required"));
    try {
      setSaving(true);
      const summary = summarizePayments(paymentCorrection.payments);
      await advancedBillingApi.correctPayments(billId, {
        payments: summary.payments,
        reason: paymentCorrection.reason.trim(),
        managerPin: paymentCorrection.managerPin || null,
        terminalId
      });
      const [paymentRows, auditRows] = await Promise.all([
        advancedBillingApi.getPayments(billId), advancedBillingApi.getAudit(billId)
      ]);
      setPayments(paymentRows || []);
      setAudit(auditRows || []);
      dispatch(fetchBill({ billId, isViewBill: 1 }));
      dispatch(closeGlobalPopup());
      closePaymentCorrection();
      toast.success(t("Payment methods corrected"));
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const ordersAndTax = { orders: billItems || [], charges: undefined };
  const roleId = Number(JSON.parse(localStorage.getItem("admin_token_info") || "{}").roleId);
  const reprintCount = audit.filter(entry => entry.action === "BILL_REPRINTED").length;
  const downloadAuditCsv = () => {
    setAuditExporting("csv");
    try {
      const headers = ["Date", "Action", "Reprint number", "Performed by", "Approved by", "Terminal", "Reason", "Field", "Old value", "New value"].map(t);
      const blob = new Blob(["\uFEFF", createCsv(headers, createAuditExportRows(audit, t))], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `bill-${billTaxDetails?.billNumber || billId}-audit.csv`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } finally { setAuditExporting(""); }
  };
  const downloadAuditPdf = async () => {
    if (!auditReportRef.current) return;
    setAuditExporting("pdf");
    try {
      await html2pdf().from(auditReportRef.current).set({ margin: 8,
        filename: `bill-${billTaxDetails?.billNumber || billId}-audit.pdf`,
        image: { type: "jpeg", quality: 0.96 }, html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }, pagebreak: { mode: ["avoid-all", "css", "legacy"] }
      }).save();
    } catch (error) { toast.error(error.message); }
    finally { setAuditExporting(""); }
  };
  const remainingRefundQuantity = item => Math.max(0, Number(item.quantity || 0) - refundHistory
    .filter(row => row.refund?.refundStatus !== "Failed")
    .flatMap(row => row.items || [])
    .filter(value => value.billItemId === item.billItemId)
    .reduce((sum, value) => sum + Number(value.quantity || 0), 0));

  // 2. Show loading indicator until bill details are fetched from API on refresh
  if (!billTaxDetails) {
    return (
      <div className="bill-page" style={{ padding: "30px", textAlign: "center" }}>
        <BackButton to="/bills" label="Back to Bills" />
        <PageLoader label="Loading bill details..." />
      </div>
    );
  }

  return (
    <div className="bill-page">
      <div className="bill-card">
        <BackButton to="/bills" label="Back to Bills" />

        <p style={{ marginTop: "15px" }}>
          <strong>Bill No.:</strong> {billTaxDetails?.billNumber || "N/A"}
        </p>

        <table className="bill-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(billItems) && billItems.length > 0 ? (
              billItems.map((item, i) => (
                <tr key={i}>
                  <td>
                    {item.name}
                    {item.addonSummary && (
                        <small style={{ color: "#6b7280", display: "block", fontSize: "11px" }}>
                          {item.addonSummary}
                        </small>
                   )}
                  </td>
                  <td>{item.quantity}</td>
                  <td>₹{(item.price || 0).toFixed(2)}</td>
                  <td>₹{((item.quantity || 0) * (item.price || 0)).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  No items found for this bill.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3">Sub Total</td>
              <td>₹{subTotal.toFixed(2)}</td>
            </tr>

            <tr>
              <td colSpan="3">Service Charge ({billTaxDetails?.serviceChargePercent || 0}%)</td>
              <td>₹{serviceChargeAmount.toFixed(2)}</td>
            </tr>

            <tr>
              <td colSpan="3">CGST ({billTaxDetails?.cgstPercent || 0}%)</td>
              <td>₹{cgstAmount.toFixed(2)}</td>
            </tr>

            <tr>
              <td colSpan="3">SGST ({billTaxDetails?.sgstPercent || 0}%)</td>
              <td>₹{sgstAmount.toFixed(2)}</td>
            </tr>

            <tr>
              <td colSpan="3">Discount</td>
              <td>- ₹{discountAmount.toFixed(2)}</td>
            </tr>

            <tr>
              <td className="bill-total" colSpan="3"><strong>Grand Total</strong></td>
              <td><strong>₹{grandTotal.toFixed(2)}</strong></td>
            </tr>

            <tr>
              <td colSpan="3">Round Off</td>
              <td>₹{roundOff.toFixed(2)}</td>
            </tr>

            <tr>
              <td className="bill-total" colSpan="3"><strong>Payable Amount</strong></td>
              <td><strong>₹{roundedTotal.toFixed(2)}</strong></td>
            </tr>

            <tr>
              <td className="bill-total" colSpan="3"><strong>Settlement Amount</strong></td>
              <td><strong>₹{settlementAmount.toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>

        {payments.length > 0 && <section className="bill-audit-section">
          <div className="payment-breakdown-heading"><div><h3>{t("Payment breakdown")}</h3><p>{t("Recorded allocations for this bill.")}</p></div>
            {[1, 2, 3, 4].includes(roleId) && billTaxDetails?.isPaid && <button type="button" onClick={openPaymentCorrection} disabled={saving}>{t("Correct payment methods")}</button>}
          </div>
          {payments.map(payment => <div className="bill-audit-row" key={payment.billPaymentId}>
            <span>{payment.paymentMode}{payment.referenceNumber ? ` · ${payment.referenceNumber}` : ""}</span>
            <strong>₹{Number(payment.amount).toFixed(2)}</strong>
            {payment.changeReturn > 0 && <small>Change ₹{Number(payment.changeReturn).toFixed(2)}</small>}
          </div>)}
        </section>}

        <div className="bill-actions">
          <input aria-label="Reprint reason" value={reprintReason} onChange={event => setReprintReason(event.target.value)} placeholder="Reason for reprint" />
          <button className="cart-button" onClick={handleRecordedPrint} disabled={saving}>Print / Reprint Bill</button>
          <button className="cart-button" onClick={() => setShowSmsPopup(true)}>📩 Send via SMS</button>
          <button className="cart-button" onClick={handleDownload}>🧾 Download PDF</button>
        </div>

        {[1, 2].includes(roleId) && <form className="bill-audit-section refund-pin-form" onSubmit={saveApprovalPin}>
          <h3>{t("Manager approval PIN")}</h3>
          <p>{t("Set a 4–8 digit PIN for approving staff or cashier refunds.")}</p>
          <div className="bill-refund-form">
            <PasswordInput required inputMode="numeric" minLength="4" maxLength="8" pattern="[0-9]+" autoComplete="new-password" toggleLabel={t("approval PIN")} placeholder={t("New approval PIN")} value={approvalPin} onChange={event => setApprovalPin(event.target.value)} />
            <button className="cart-button" disabled={saving}>{t("Save PIN")}</button>
          </div>
        </form>}

        {[1, 2, 3, 4].includes(roleId) && !billTaxDetails?.isPaid &&
          billTaxDetails?.billStatus !== "Void" && <form className="bill-audit-section" onSubmit={submitItemVoid}>
            <h3>{t("Void bill items")}</h3>
            <p>{t("Select the quantity that must be removed. The bill totals and kitchen adjustment will be updated.")}</p>
            <div className="refund-item-grid">
              {(billItems || []).filter(item => item.quantity > 0).map(item => <label key={item.billItemId} className="refund-item-row">
                <span>{item.name}<small>{t("Available")}: {item.quantity}</small></span>
                <input aria-label={`Void quantity for ${item.name}`} type="number" min="0" max={item.quantity} step="1"
                  value={voidRequest.items.find(value => value.billItemId === item.billItemId)?.quantity || ""}
                  onChange={event => updateVoidItem(item.billItemId, event.target.value)} placeholder="Qty" />
              </label>)}
            </div>
            <div className="bill-refund-form">
              <input required placeholder={t("Void reason")} value={voidRequest.reason} onChange={event => setVoidRequest(current => ({ ...current, reason: event.target.value }))} />
              {[3, 4].includes(roleId) && <PasswordInput required inputMode="numeric" autoComplete="off" toggleLabel={t("manager approval PIN")} placeholder={t("Manager approval PIN")} value={voidRequest.managerPin} onChange={event => setVoidRequest(current => ({ ...current, managerPin: event.target.value }))} />}
              <button className="cart-button" disabled={saving}>{t("Void selected quantity")}</button>
            </div>
          </form>}

        {[1, 2, 3, 4].includes(roleId) && billTaxDetails?.isPaid &&
          billTaxDetails?.billStatus !== "Refunded" && <form className="bill-audit-section" onSubmit={submitRefund}>
            <h3>{t("Refund payment")}</h3>
            <p>{t("Select item quantities for an item-wise refund, or leave them empty and enter a custom refund amount.")}</p>
            <div className="refund-item-grid">
              {(billItems || []).filter(item => remainingRefundQuantity(item) > 0).map(item => <label key={item.billItemId} className="refund-item-row">
                <span>{item.name}<small>{t("Available")}: {remainingRefundQuantity(item)}</small></span>
                <input aria-label={`Refund quantity for ${item.name}`} type="number" min="0" max={remainingRefundQuantity(item)} step="1"
                  value={refund.items.find(value => value.billItemId === item.billItemId)?.quantity || ""}
                  onChange={event => updateRefundItem(item.billItemId, event.target.value)} placeholder="Qty" />
              </label>)}
            </div>
            <div className="bill-refund-form">
              <input required={refund.items.length === 0} disabled={refund.items.length > 0} type="number" min="0.01" step="0.01" placeholder={t(refund.items.length ? "Amount calculated from items" : "Refund amount")} value={refund.amount} onChange={event => setRefund(current => ({ ...current, amount: event.target.value }))} />
              <select value={refund.refundMode} onChange={event => setRefund(current => ({ ...current, refundMode: event.target.value }))}>
                <option value="CASH">Cash</option><option value="CARD">Card</option><option value="UPI">UPI</option><option value="OTHER">Other</option><option value="ONLINE">Online</option>
              </select>
              <input placeholder={t("Reference (optional)")} value={refund.referenceNumber} onChange={event => setRefund(current => ({ ...current, referenceNumber: event.target.value }))} />
              <input required placeholder={t("Refund reason")} value={refund.reason} onChange={event => setRefund(current => ({ ...current, reason: event.target.value }))} />
              {[3, 4].includes(roleId) && <PasswordInput required inputMode="numeric" autoComplete="off" toggleLabel={t("manager approval PIN")} placeholder={t("Manager approval PIN")} value={refund.managerPin} onChange={event => setRefund(current => ({ ...current, managerPin: event.target.value }))} />}
              <button className="cart-button" disabled={saving}>{t("Record refund")}</button>
            </div>
          </form>}

        {refundHistory.length > 0 && <section className="bill-audit-section">
          <h3>{t("Refund history")}</h3>
          {refundHistory.map(row => { const item = row.refund || {}; return <div className="bill-audit-row refund-history-row" key={item.billRefundId}>
            <span><strong>₹{Number(item.amount || 0).toFixed(2)}</strong> · {item.refundMode}</span>
            <span className={`refund-status refund-status--${String(item.refundStatus || "").toLowerCase()}`}>{t(item.refundStatus)}</span>
            <small>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}</small>
            {item.failureReason && <span className="refund-error">{item.failureReason}</span>}
            {(row.items || []).length > 0 && <small>{row.items.map(value => `${value.itemName} × ${value.quantity}`).join(", ")}</small>}
            <div className="refund-history-actions">
              {item.refundStatus === "Completed" && <button type="button" onClick={() => printRefundReceipt(item.billRefundId)}>{t("Print receipt")}</button>}
              {item.refundMode === "ONLINE" && item.refundStatus === "Pending" && <button type="button" disabled={saving} onClick={() => refreshRefund(item.billRefundId)}>{t("Refresh status")}</button>}
            </div>
          </div>; })}
        </section>}

        <section className="bill-audit-section bill-audit-shell">
          <div className="audit-toolbar"><div><h3>{t("Audit report")}</h3><p>{t("Complete bill activity with staff, terminal and value changes.")}</p></div><span className="audit-total-badge">{t("Reprints")}: {reprintCount}</span><div className="audit-export-actions">
            <button type="button" onClick={downloadAuditCsv} disabled={!audit.length || Boolean(auditExporting)} aria-busy={auditExporting === "csv"}>{auditExporting === "csv" ? <ButtonLoader label={t("Preparing...")} /> : <><FileSpreadsheet size={16} />{t("Export CSV")}</>}</button>
            <button type="button" onClick={handleAuditPrint} disabled={!audit.length || Boolean(auditExporting)}><Printer size={16} />{t("Print audit")}</button>
            <button type="button" onClick={downloadAuditPdf} disabled={!audit.length || Boolean(auditExporting)} aria-busy={auditExporting === "pdf"}>{auditExporting === "pdf" ? <ButtonLoader label={t("Preparing...")} /> : <><Download size={16} />{t("Download PDF")}</>}</button>
          </div></div>
          <AuditReport ref={auditReportRef} audit={audit} billNumber={billTaxDetails?.billNumber} t={t} />
        </section>
      </div>

      <GlobalPopup
        popupKey="payment-correction"
        title={t("Correct payment methods")}
        onClose={closePaymentCorrection}
        disableClose={saving}
        footer={(closePopup) => <>
          <button type="button" className="popup-action-btn secondary" onClick={closePopup} disabled={saving}>{t("Cancel")}</button>
          <button type="submit" form="payment-correction-form" className="popup-action-btn"
            disabled={saving || !paymentCorrection.reason.trim()} aria-busy={saving}>
            {saving ? <ButtonLoader label={t("Saving...")} /> : t("Save correction")}
          </button>
        </>}
      >
        <form id="payment-correction-form" className="payment-correction-form" onSubmit={submitPaymentCorrection}>
          <p>{t("The paid amount stays unchanged. Update only the payment allocation and enter a reason for the audit trail.")}</p>
          <PaymentAllocationEditor payableAmount={correctionPayable} payments={paymentCorrection.payments}
            onChange={next => setPaymentCorrection(current => ({ ...current, payments: next }))} disabled={saving} />
          <label><span>{t("Correction reason")}</span><textarea required autoFocus maxLength="500" value={paymentCorrection.reason} disabled={saving}
            onChange={event => setPaymentCorrection(current => ({ ...current, reason: event.target.value }))} /></label>
          {[3, 4].includes(roleId) && <label><span>{t("Manager approval PIN")}</span><PasswordInput required inputMode="numeric" autoComplete="off" toggleLabel={t("manager approval PIN")}
            value={paymentCorrection.managerPin} disabled={saving}
            onChange={event => setPaymentCorrection(current => ({ ...current, managerPin: event.target.value }))} /></label>}
        </form>
      </GlobalPopup>

      {showSmsPopup && (
        <SmsPopup billId={billId} onClose={() => setShowSmsPopup(false)} />
      )}

      <div className="refund-receipt-host"><RefundReceipt ref={refundReceiptRef} data={refundReceipt} /></div>

      <PrintBill
        ref={componentRef}
        OrderAndChargesDetails={ordersAndTax}
        tableName={tableName}
        subTotal={subTotal}
        cgstAmount={cgstAmount}
        sgstAmount={sgstAmount}
        serviceChargeAmount={serviceChargeAmount}
        appliedDiscount={discountAmount}
        grandTotal={grandTotal}
        orderMode={OrderType}
        billDetails={billTaxDetails}
        roundOff={roundOff}
        payableAmount={roundedTotal}
      />
    </div>
  );
};

export default ViewBill;
